import { ContractState, VehicleOrderStatus, VehicleOrderViewModel } from '@ail/orders-api'
import { useCallback, useMemo } from 'react'
import { hasTimeElapsed } from '../helpers/Utils'
import { useGetVehicleOrdersQuery } from '../redux/api/ordersApi'
import { useAuth } from './useAuth'
import { useGridFilters } from './useGridFilters'

export type VehicleOrderStats = Record<string, { actionRequired: number; frozen: number }>

export interface WithVehicleOrderStats {
  vehicleOrderStats: VehicleOrderStats
  updateFilterValue: (args: { newFilters: { [filterKey: string]: unknown }; gridId: string }) => void
}
export function useVehicleOrderStats() {
  const { user } = useAuth()
  const { data: vehicleOrders } = useGetVehicleOrdersQuery()

  const isUserActionRequired = useCallback(
    (order?: VehicleOrderViewModel) => {
      const maxDurationPassed = hasTimeElapsed(order?.earliestDelivery, 45)
      const isPendingConfirmation =
        order?.status === VehicleOrderStatus.PendingDeliveryConfirmation && !maxDurationPassed
      const isUndeliveredInternal = order?.isUndelivered && user?.isInternalUser
      const isExpiring =
        order?.isConfirmationExpiring &&
        order?.status !== VehicleOrderStatus.Delivered &&
        (user?.isInternalUser || !maxDurationPassed)
      return (isPendingConfirmation || isUndeliveredInternal || isExpiring) ?? false
    },
    [user?.isInternalUser]
  )

  const isOrderFrozen = useCallback((vehicleOrder?: VehicleOrderViewModel) => {
    return (
      !!vehicleOrder?.freeze &&
      hasTimeElapsed(vehicleOrder.freeze, 0) &&
      !vehicleOrder.earliestDelivery &&
      vehicleOrder.status !== VehicleOrderStatus.Delivered &&
      vehicleOrder.status !== VehicleOrderStatus.Delivering
    )
  }, [])

  const orderStats = useMemo(() => {
    if (!vehicleOrders) return
    const stats = vehicleOrders
      .filter((o) => o.contractState === ContractState.Initialized || o.contractState === ContractState.Running)
      .reduce((stats, o) => {
        const customerId = getCustomerId(o.owner)
        if (!customerId) {
          return stats
        }
        if (!stats[customerId]) {
          stats[customerId] = { actionRequired: 0, frozen: 0 }
        }
        if (isOrderFrozen(o)) {
          stats[customerId].frozen++
        } else if (isUserActionRequired(o)) {
          stats[customerId].actionRequired++
        }
        return stats
      }, {} as VehicleOrderStats)

    const filtered = Object.fromEntries(
      Object.entries(stats).filter(([, val]) => val.actionRequired > 0 || val.frozen > 0)
    ) as VehicleOrderStats
    return Object.keys(filtered).length > 0 ? filtered : undefined
  }, [isOrderFrozen, isUserActionRequired, vehicleOrders])
  return orderStats
}

export function withVehicleOrderStats<P extends object>(WrappedComponent: React.ComponentType<P>) {
  return (props: P) => {
    const vehicleOrderStats = useVehicleOrderStats()
    const [updateFilterValue] = useGridFilters()
    return <WrappedComponent {...props} vehicleOrderStats={vehicleOrderStats} updateFilterValue={updateFilterValue} />
  }
}

const getCustomerId = (owner?: string) => owner?.replace('$', '').trim().toLowerCase().split('//')[0] || ''
